'use client';

import Image from 'next/image';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useRouter, usePathname } from 'next/navigation';
import SvgIcons from '../components/SvgIcons';
import logo from './images/cleanlogo.png';
import { useState, useEffect, useRef } from 'react';
import productData from './ProductData';

const Navbar = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isProductsSubmenuOpen, setProductsSubmenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);


  const handleMenuToggle = () => {
    setMenuOpen(!isMenuOpen);
    if (isProductsSubmenuOpen) setProductsSubmenuOpen(false);
  };

  const handleProductSubmenuToggle = () => {
    setProductsSubmenuOpen(!isProductsSubmenuOpen);
  };

  const handleProductClick = (url) => {
    router.push(`/products/${url}`);
    setMenuOpen(false);
    setProductsSubmenuOpen(false);
  };

  // Prevent background scrolling when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto'; // Cleanup on unmount
    };
  }, [isMenuOpen]);

  

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setProductsSubmenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="bg-white shadow-[0_4px_4px_rgba(0,0,0,0.1)] !sticky !top-0 !z-[999999]">
      <div className="px-4 lg:px-0 lg:container mx-auto">
        <div className="flex items-center justify-center md:!justify-between bg-white py-3.5">
          <div className="flex items-center md:!gap-10 gap-[40px]">
            <p>
              <a aria-label='phone number of the clean flow sysytem' href="tel:+91 78100 06665" className="flex font-['Neue_Montreal'] items-center gap-1 text-[#016767] text-[12px] md:text-[16px] md:!font-medium font-dm-sans">
                <SvgIcons.Call /> 78100 06665
              </a>
            </p>
            <p>
              <a  aria-label='email of the clean flow sysytem' href="mailto:cleanflowsystems@gmail.com" className="flex font-['Neue_Montreal'] items-center gap-1 text-[#016767] text-[12px] md:text-[16px] md:!font-medium font-dm-sans">
                <SvgIcons.Mail /> cleanflowsystems@gmail.com
              </a>
            </p>
          </div>
          <div className="md:!flex items-center gap-6 hidden">
            <a aria-label='Facebook of the clean flow sysytem'  href="https://www.facebook.com/profile.php?id=100070193553673" target='_blank'><SvgIcons.Facebook /></a>
            <a aria-label='Youtube of the clean flow sysytem' href="/"><SvgIcons.Youtube /></a>
            <a aria-label='x of the clean flow sysytem' href="/"><SvgIcons.X /></a>
            <a aria-label='Insta of the clean flow sysytem' href="https://www.instagram.com/cleanflow_systems/" target='_blank'><SvgIcons.Insta /></a>
          </div>
        </div>
        <hr className="h-[0.6px] bg-[#E2E2E2]" />

        <div className="flex items-center justify-between bg-white py-2.5">
          <div className='block md:!hidden'></div>
          <div className='w-[73px] md:w-[120px]'>
            <a href="/">
              <Image src={logo} alt="Clean Logo" priority className="object-contain" />
            </a>
          </div>

          <div className="md:!hidden block cursor-pointer" onClick={handleMenuToggle}>
            {isMenuOpen ? (
              <FaTimes className="text-2xl text-black" />
            ) : (
              <FaBars className="text-2xl" />
            )}
          </div>

          {isMenuOpen && (
            <div
              className="fixed !top-[100px] inset-0 bg-black bg-opacity-50 z-[999] block md:!hidden"
              onClick={handleMenuToggle}
            ></div>
          )}

          <div className="md:!flex !hidden">
            <ul className="flex items-start justify-start list-none p-0 m-0">
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/')}
              >
                Home
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  (pathname.startsWith('/products') || pathname === '/all-products')
                    ? 'text-[#FF8B03] font-bold'
                    : 'text-black'
                } hover:text-[#FF8B03] relative group after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
              >
                <span className="flex items-center gap-1">
                  Products <SvgIcons.DownArrow />
                </span>
                <ul
                  className="absolute z-[1000] top-full left-0 !w-60 bg-white shadow-lg opacity-0 invisible group-hover:!opacity-100 group-hover:!visible transition-all duration-300 !important"
                >
                  <li className="md:px-2 lg:px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm !text-black !font-medium">
                    <a href="/all-products">All Products</a>
                  </li>
                  {productData.products.map((product) => (
                    <li
                      key={product.id}
                      onClick={() => handleProductClick(product.url)}
                      className="md:px-2 lg:px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm !text-black !font-medium"
                    >
                      {product.title}
                    </li>
                  ))}
                </ul>
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/services' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/services')}
              >
                Services
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/about-us' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/about-us')}
              >
                About
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/contact-us' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/contact-us')}
              >
                Contact
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/cleanroom-blog' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/cleanroom-blog')}
              >
                Blog
              </li>
              <li
                className={`flex items-center md:px-2 lg:px-4 py-2 md:text-[14px] lg:text-base font-medium font-['Neue_Montreal'] cursor-pointer ${
                  pathname === '/gallery' ? 'text-[#FF8B03] font-bold' : 'text-black'
                } hover:text-[#FF8B03] relative after:content-[''] after:absolute after:left-0 after:bottom-[-5px] after:h-[2px] after:bg-[#FF8B03] after:w-0 after:transition-[width] after:duration-300 hover:after:w-full`}
                onClick={() => router.push('/gallery')}
              >
                Gallery
              </li>
            </ul>
          </div>

          <div className="md:!block !hidden">

              <a 
              className="!bg-[#FF8B03] text-[14px] whitespace-nowrap lg:text-[16px] !hidden md:!block text-white md:px-2 lg:px-4 py-2 rounded hover:bg-[#e07b00]"
               aria-label='contact us button' href="/contact-us">Call us</a>
          </div>
        </div>

        <div
          ref={menuRef}
          className={`fixed top-[99px] right-0 w-64 h-full bg-[#fff] text-black p-[16px] transition-transform duration-300 ease-in-out z-[1000] md:!hidden ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          
        >
          <ul className="">
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/');
                setMenuOpen(false);
              }}
            >
              Home
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname.startsWith('/products') || pathname === '/all-products'
                  ? 'text-[#FF8B03] font-bold'
                  : 'text-black'
              } hover:text-[#FF8B03]`}
            >
              <span
                className="flex items-center gap-1"
                onClick={handleProductSubmenuToggle}
              >
                Products <SvgIcons.DownArrow />
              </span>
              <ul
                className={`bg-[#F5F5F5] text-black !px-6 !py-2 !w-full transition-all duration-300 max-h-[60vh] overflow-y-auto ${
                  isProductsSubmenuOpen ? 'block' : 'hidden'
                }`}
              >
                <li className="md:px-2 lg:px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm !text-black !font-medium">
                  <a href="/all-products">All Products</a>
                </li>
                {productData.products.map((product) => (
                  <li
                    key={product.id}
                    onClick={() => handleProductClick(product.url)}
                    className="py-2 hover:bg-gray-100 cursor-pointer text-sm !font-medium !text-black"
                  >
                    {product.title}
                  </li>
                ))}
              </ul>
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/services' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/services');
                setMenuOpen(false);
              }}
            >
              Services
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/about-us' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/about-us');
                setMenuOpen(false);
              }}
            >
              About
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/contact-us' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/contact-us');
                setMenuOpen(false);
              }}
            >
              Contact
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/cleanroom-blog' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/cleanroom-blog');
                setMenuOpen(false);
              }}
            >
              Blog
            </li>
            <li
              className={`py-3 cursor-pointer ${
                pathname === '/gallery' ? 'text-[#FF8B03] font-bold' : 'text-black'
              } hover:text-[#FF8B03]`}
              onClick={() => {
                router.push('/gallery');
                setMenuOpen(false);
              }}
            >
              Gallery
            </li>
          </ul>

          <div className="">

             <a   className="!bg-[#FF8B03] text-[14px] whitespace-nowrap lg:text-[16px] text-white !px-6 !py-2 rounded hover:bg-[#e07b00]"
              aria-label='contact us button' href="/contact-us">Call us</a>
          </div>

          <div className="!flex items-center gap-6 !mt-[16px]">
            <a aria-label='Facebook icon of clean flow system' href="https://www.facebook.com/profile.php?id=100070193553673" target='_blank'><SvgIcons.Facebook /></a>
            {/* <a href="/"><SvgIcons.Youtube /></a> */}
            {/* <a href="/"><SvgIcons.X /></a> */}
            <a aria-label='Insta icon of clean flow system' href="https://www.instagram.com/cleanflow_systems/" target='_blank'><SvgIcons.Insta /></a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
